import * as React from "react";
import { AppShell, Footer, Header, Navbar } from "@mantine/core";

import { useMediaQuery } from "@mantine/hooks";
import { ReactNode, useState } from "react";

import _InternalNavItem, { _InternalNavElement } from "./_InternalNavItem";
import {
	_defaultCopyright,
	_defaultImpressum,
	_defaultPrivacyPolicy,
	_mobileWidth,
} from "../constants";
import _Footer from "./_Footer";
import _Header from "./_Header";

export interface VSETHInternalAppProps {
	/**
	 * Title of the app displayed in the header
	 */
	title: string;
	/**
  	List of Nav Items displayed in the sidebar
  	*/
	nav: _InternalNavElement[];
	/**
	Function to wrap an element of the Nav into a custom Link component
  	*/
	makeWrapper?: (
		url: string | undefined,
		child: ReactNode
	) => ReactNode | JSX.Element;
	/**
	Path of current page 
  	*/
	activeHref: string;
	/**
	Link to disclaimer page
  	*/
	disclaimer?: string;
	/**
	Link to privacy policy page
  	*/
	privacyPolicy?: string;
	/**
	Copyright statement
  	*/
	copyright?: string;
	/**
	Children rendered in the main area
  	*/
	children?: React.ReactNode;
}

const VSETHInternalApp: React.FC<VSETHInternalAppProps> = ({
	title,
	nav,
	makeWrapper = (url: string | undefined, child: ReactNode) => (
		<a href={url} style={{ textDecoration: "none", color: "inherit" }}>
			{child}
		</a>
	),
	activeHref,
	disclaimer = _defaultImpressum,
	privacyPolicy = _defaultPrivacyPolicy,
	copyright = _defaultCopyright,
	children,
}) => {
	const [sidebarHidden, setSidebarHidden] = useState(true);
	const [sidebarMinimized, setSidebarMinimized] = useState(false);

	const mobile = useMediaQuery(`(max-width: ${_mobileWidth}px)`);

	return (
		<AppShell
			navbarOffsetBreakpoint={_mobileWidth}
			fixed={true}
			styles={(theme: any) => ({
				main: {
					backgroundColor: theme.colors.gray[1],
				},
			})}
			header={
				<Header height={69} p="md">
					<_Header
						title={title}
						sidebarHidden={sidebarHidden}
						toggleSidebarHidden={() => setSidebarHidden((hidden) => !hidden)}
						makeWrapper={makeWrapper}
					/>
				</Header>
			}
			navbar={
				<Navbar
					width={{ xs: sidebarMinimized ? 62 : 235 }}
					hiddenBreakpoint={_mobileWidth}
					hidden={sidebarHidden}
				>
					{nav.map((navItem) => (
						<_InternalNavItem
							navElement={navItem}
							key={navItem.label}
							minimal={sidebarMinimized && !mobile}
							makeWrapper={makeWrapper}
							activeHref={activeHref}
							onClickItem={() => setSidebarHidden(true)}
						/>
					))}
				</Navbar>
			}
			footer={
				<Footer height={62} p="md">
					<_Footer
						toggleSidebarMinimized={() =>
							setSidebarMinimized((minimized) => !minimized)
						}
						sidebarMinimized={sidebarMinimized}
						disclaimer={disclaimer}
						privacyPolicy={privacyPolicy}
						copyright={copyright}
					/>
				</Footer>
			}
		>
			{children}
		</AppShell>
	);
};

export default VSETHInternalApp;
